import { useState } from 'react';
import { ChevronDown, Eye } from 'lucide-react';

import { Collapse } from '@/components/Collapse';
import { cn } from '@/lib/cn';
import type { CalendarAccount } from './types';
import { calendarDisplayName } from './types';

interface Props {
  accounts: CalendarAccount[];
  hidden: Set<number>;
  onToggle: (calendarId: number) => void;
  onShowAll: () => void;
}

/**
 * Show/hide imported calendars on the Plan grid.
 *
 * A view filter, not a settings change: hiding a calendar takes it off the
 * screen but its events still count as busy, so a hidden lunch meeting cannot
 * quietly become schedulable time. Sync membership stays in Settings, where a
 * change has consequences worth a trip.
 */
export function CalendarVisibility({ accounts, hidden, onToggle, onShowAll }: Props) {
  const [open, setOpen] = useState(false);

  const calendars = accounts.flatMap((account) =>
    account.calendars
      .filter((c) => c.selected)
      .map((calendar) => ({ calendar, account })),
  );

  if (calendars.length === 0) return null;

  const shown = calendars.filter((c) => !hidden.has(c.calendar.id)).length;

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={cn(
          'flex w-full items-center gap-2 rounded px-1 py-1.5 text-left',
          'text-small font-medium text-text-secondary outline-none focus-visible:ring-2 focus-visible:ring-ring',
          'hover:text-text',
        )}
      >
        <Eye size={14} strokeWidth={1.5} aria-hidden />
        <span className="flex-1">Calendars</span>
        <span className="tabular text-label text-text-tertiary">
          {shown}/{calendars.length}
        </span>
        <ChevronDown
          size={14}
          strokeWidth={1.5}
          aria-hidden
          className={cn('transition-transform duration-press', open && 'rotate-180')}
        />
      </button>

      <Collapse open={open}>
        <ul className="mt-1 flex flex-col gap-0.5 pb-1">
          {calendars.map(({ calendar, account }) => {
            const visible = !hidden.has(calendar.id);
            return (
              <li key={calendar.id}>
                <label
                  className={cn(
                    'flex cursor-pointer items-center gap-2 rounded px-2 py-1.5',
                    'text-small hover:bg-surface-2',
                    visible ? 'text-text' : 'text-text-tertiary',
                  )}
                >
                  <input
                    type="checkbox"
                    checked={visible}
                    onChange={() => onToggle(calendar.id)}
                    className="size-check shrink-0 accent-[var(--accent)]"
                  />
                  <span
                    aria-hidden
                    className={cn('size-dot shrink-0 rounded-full', !visible && 'opacity-40')}
                    style={{ backgroundColor: calendar.color ?? 'var(--border-strong)' }}
                  />
                  <span className="min-w-0 flex-1 truncate">
                    {calendarDisplayName(calendar)}
                    <span className="block text-label text-text-tertiary">
                      {account.label}
                    </span>
                  </span>
                </label>
              </li>
            );
          })}
        </ul>

        {shown < calendars.length && (
          <div className="pb-1">
            <button
              type="button"
              onClick={onShowAll}
              className="rounded px-2 py-1 text-label text-accent outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring"
            >
              Show all
            </button>
            <p className="px-2 pt-1 text-label text-text-tertiary">
              Hidden calendars still count as busy.
            </p>
          </div>
        )}
      </Collapse>
    </div>
  );
}
